import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Select,
  Spinner,
} from "@empite/ui-components";
import type {
  NewProviderTemplate,
  ProviderAuthStyle,
  ProviderTemplate,
  ProviderTemplateEdit,
  ProviderTemplateStatus,
  Reason,
} from "../admin-client";
import {
  useCreateProviderTemplate,
  useProviderTemplates,
  useSetProviderTemplateStatus,
  useUpdateProviderTemplate,
} from "../admin-hooks";
import { ErrorAlert, RequireOperator, isPlatformStaff } from "./guards";
import { ReasonButton } from "./reason-prompt";
import { StatusBadge, when } from "./common";

/**
 * Provider templates — what the PLATFORM says a provider is.
 *
 * This is the half of M3 a super admin owns, and it is a different screen from
 * Providers next door on purpose. A template is an endpoint, an auth style and
 * a name; an account is a tenant's own key. Showing them in one table taught
 * operators that adding a provider and defining one were the same act, which
 * is exactly how every tenant ended up spending the platform's keys.
 *
 * PLATFORM-ONLY, and the API says so twice: the write routes are guarded with
 * model_catalogue:write, which AllowedInScope refuses to a tenant-confined
 * subject however senior it is, and the store refuses again in platformOnly.
 * This screen is the third and weakest of the three — a courtesy, so a tenant
 * operator does not discover the rule as an audited 403.
 */

/** The adapters this build carries, in the order internal/providers registers
 *  them. Repeated from the models screen for the same reason it repeats it
 *  there: to decide what to DRAW. The server resolves the submitted name
 *  against the registry and refuses anything else, because a template naming a
 *  provider with no adapter is a picker entry that resolves to nothing at the
 *  moment a customer's prompt needs it. */
const PROVIDERS = ["anthropic", "openrouter", "openai", "gemini"] as const;

/** The header a tenant's key is presented in, said in the operator's words.
 *  A Record keyed by the union, so widening ProviderAuthStyle fails to
 *  typecheck until the new style is labelled rather than silently missing
 *  from the picker. */
const AUTH_STYLES: Record<ProviderAuthStyle, string> = {
  bearer: "Authorization: Bearer <key>",
  "x-api-key": "X-Api-Key: <key>",
  "x-goog-api-key": "X-Goog-Api-Key: <key>",
};

const AUTH_STYLE_NAMES = Object.keys(AUTH_STYLES) as ProviderAuthStyle[];

/** What each status change means. `retired` is the one that changes what
 *  operators can do, and it changes nothing about the accounts already made —
 *  the sentence says so because the button cannot. */
const STATUS_ACTIONS: Array<{
  status: ProviderTemplateStatus;
  label: string;
  danger: boolean;
  description: string;
}> = [
  {
    status: "published",
    label: "Publish",
    danger: false,
    description:
      "Every tenant can see this provider and add an account of their own against it. It is the only status an account can be instantiated from.",
  },
  {
    status: "draft",
    label: "Return to draft",
    danger: false,
    description:
      "The definition stays, and stops being offered. Tenants who have already added an account against it keep serving traffic on their own keys — this only closes the picker.",
  },
  {
    status: "retired",
    label: "Retire",
    danger: true,
    description:
      "Nobody can add this provider any more, and the row stays to answer why. Accounts already instantiated from it are untouched: retiring a template must never take a tenant's working upstream away from them.",
  },
];

/** The headers box is `Name: value`, one per line — the whole map, so an
 *  operator who deletes a line has deleted a header. Anything a credential
 *  could travel in is refused by the API by name, and lower-cased there, so
 *  the console does not need to re-state the rule to state the shape. */
function headerLines(headers: Record<string, string>): string {
  return Object.entries(headers)
    .map(([name, value]) => `${name}: ${value}`)
    .join("\n");
}

/** Parses the box back. A line with no colon is the error, because "the value
 *  is empty" and "you forgot the colon" are different mistakes and only one of
 *  them is about the header's contents. */
function parseHeaderLines(
  raw: string,
): { headers: Record<string, string> } | { error: string } {
  const headers: Record<string, string> = {};
  for (const line of raw.split("\n")) {
    const text = line.trim();
    if (text === "") {
      continue;
    }
    const at = text.indexOf(":");
    if (at <= 0) {
      return { error: `"${text}" is not "Name: value".` };
    }
    const name = text.slice(0, at).trim();
    const value = text.slice(at + 1).trim();
    if (name === "") {
      return { error: `"${text}" has no header name.` };
    }
    headers[name] = value;
  }
  return { headers };
}

export function ProviderTemplates() {
  return (
    <RequireOperator>
      {(session) =>
        isPlatformStaff(session.operator) ? (
          <TemplateConsole />
        ) : (
          <>
            <PageHeader title="Provider templates" />
            <Card>
              <Alert tone="info" title="This catalogue belongs to the platform">
                <p>
                  A template is the platform&rsquo;s statement about a provider
                  — where it lives and how a key is presented to it — and it is
                  shared by every tenant, so only platform staff maintain it.
                  What you choose to run on, with your own key, is on{" "}
                  <strong>Providers</strong>.
                </p>
              </Alert>
            </Card>
          </>
        )
      }
    </RequireOperator>
  );
}

type FormTarget =
  | { mode: "create" }
  | { mode: "edit"; row: ProviderTemplate }
  | null;

function TemplateConsole() {
  const templates = useProviderTemplates();
  const setStatus = useSetProviderTemplateStatus();
  const [target, setTarget] = useState<FormTarget>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (templates.isPending) {
    return <Spinner label="Loading provider templates" />;
  }
  if (templates.error) {
    return (
      <ErrorAlert
        error={templates.error}
        title="Could not load the provider templates"
      />
    );
  }

  const rows = templates.data?.provider_templates ?? [];

  return (
    <>
      <PageHeader
        title="Provider templates"
        description="The providers this platform offers: the endpoint, and the header a tenant's own key is presented in. No credential is ever held here."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setNotice(null);
              setTarget({ mode: "create" });
            }}
          >
            Add template
          </Button>
        }
      />
      {notice ? (
        <Alert tone="success" title="Saved">
          <p>{notice}</p>
        </Alert>
      ) : null}
      <ErrorAlert
        error={setStatus.error}
        title="Could not change the template's status"
      />
      {target ? (
        <Card>
          <TemplateForm
            target={target}
            onClose={() => setTarget(null)}
            onDone={(message) => {
              setNotice(message);
              setTarget(null);
            }}
          />
        </Card>
      ) : null}
      <Card>
        <DataTable<ProviderTemplate>
          rows={rows}
          rowKey={(row) => row.id}
          empty="No provider has been defined yet. Until one is published, no tenant can add an upstream of their own."
          columns={[
            {
              key: "display_name",
              header: "Provider",
              render: (row) => (
                <>
                  <div>{row.display_name}</div>
                  <code className="esm-muted-text">{row.provider}</code>
                </>
              ),
            },
            {
              key: "base_url",
              header: "Endpoint",
              // The origin, not an endpoint: each adapter composes its own
              // path onto it, which is why the API refuses one here.
              render: (row) => <code>{row.base_url}</code>,
            },
            {
              key: "auth_style",
              header: "Key travels in",
              render: (row) => (
                <code>{AUTH_STYLES[row.auth_style] ?? row.auth_style}</code>
              ),
            },
            {
              key: "headers",
              header: "Default headers",
              align: "end",
              render: (row) => {
                const names = Object.keys(row.default_headers);
                return names.length === 0 ? (
                  <span className="esm-muted-text">—</span>
                ) : (
                  <Badge tone="neutral">{names.join(", ")}</Badge>
                );
              },
            },
            {
              key: "status",
              header: "Status",
              render: (row) => <StatusBadge status={row.status} />,
            },
            {
              key: "updated",
              header: "Updated",
              render: (row) => when(row.updated_at),
            },
            {
              key: "actions",
              header: "",
              align: "end",
              render: (row) => (
                <div className="esm-actions esm-actions--table">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setNotice(null);
                      setTarget({ mode: "edit", row });
                    }}
                  >
                    Edit
                  </Button>
                  {STATUS_ACTIONS.filter(
                    (action) => action.status !== row.status,
                  ).map((action) => (
                    <ReasonButton
                      key={action.status}
                      variant={action.danger ? "danger" : "secondary"}
                      busy={setStatus.isPending}
                      mutation={{
                        title: `${action.label}: ${row.display_name}`,
                        tone: action.danger ? "danger" : "primary",
                        description: <p>{action.description}</p>,
                        confirmLabel: action.label,
                        run: async (reason: Reason) => {
                          const result = await setStatus.mutateAsync({
                            templateID: row.id,
                            status: action.status,
                            reason,
                          });
                          setNotice(
                            `${result.provider_template.display_name} is now ${result.provider_template.status}.`,
                          );
                        },
                      }}
                    >
                      {action.label}
                    </ReasonButton>
                  ))}
                </div>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}

interface TemplateFormState {
  provider: string;
  display_name: string;
  base_url: string;
  auth_style: ProviderAuthStyle;
  headers: string;
  status: "draft" | "published";
}

function initialForm(target: Exclude<FormTarget, null>): TemplateFormState {
  if (target.mode === "create") {
    return {
      provider: PROVIDERS[0],
      display_name: "",
      base_url: "",
      auth_style: "bearer",
      headers: "",
      status: "published",
    };
  }
  return {
    provider: target.row.provider,
    display_name: target.row.display_name,
    base_url: target.row.base_url,
    auth_style: target.row.auth_style,
    headers: headerLines(target.row.default_headers),
    // Never sent on an edit: status moves through its own route, which is
    // what makes "who retired this, and why" a row in the audit trail.
    status: "published",
  };
}

function TemplateForm({
  target,
  onClose,
  onDone,
}: {
  target: Exclude<FormTarget, null>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const create = useCreateProviderTemplate();
  const update = useUpdateProviderTemplate();
  const [form, setForm] = useState<TemplateFormState>(() =>
    initialForm(target),
  );
  const creating = target.mode === "create";

  const set = (field: keyof TemplateFormState) => (value: string) =>
    setForm((current) => ({ ...current, [field]: value }));

  const parsed = parseHeaderLines(form.headers);
  const headerError = "error" in parsed ? parsed.error : undefined;
  // https only, and an ORIGIN. Mirrored from validateTemplateBaseURL so the
  // operator hears the rule while typing: http is refused even for local
  // convenience, because the place that needs plaintext — a tenant's own
  // gateway, or the mock — overrides on the ACCOUNT instead.
  const urlError = baseURLError(form.base_url);
  const ready =
    form.display_name.trim() !== "" &&
    form.base_url.trim() !== "" &&
    headerError === undefined &&
    urlError === undefined;

  const label = creating ? "Add template" : "Save changes";
  const name = form.display_name.trim() || form.provider;

  async function run(reason: Reason) {
    const headers = "headers" in parsed ? parsed.headers : {};
    if (creating) {
      const body: NewProviderTemplate = {
        provider: form.provider,
        display_name: form.display_name.trim(),
        base_url: form.base_url.trim(),
        auth_style: form.auth_style,
        default_headers: headers,
        status: form.status,
      };
      const result = await create.mutateAsync({ body, reason });
      onDone(
        `${result.provider_template.display_name} is defined as ${result.provider_template.provider}, ${result.provider_template.status}.`,
      );
      return;
    }
    // The provider key is NOT here. It is what already-instantiated accounts
    // and the model catalogue join on, so moving it would silently re-address
    // them — the API answers a body that tries with a 409, and this form does
    // not offer the box.
    const body: ProviderTemplateEdit = {
      display_name: form.display_name.trim(),
      base_url: form.base_url.trim(),
      auth_style: form.auth_style,
      // Sent whole, always: the box holds the entire map, so a header the
      // operator deleted has to arrive as an absence rather than as silence.
      default_headers: headers,
    };
    const result = await update.mutateAsync({
      templateID: target.row.id,
      body,
      reason,
    });
    onDone(`Updated ${result.provider_template.display_name}.`);
  }

  return (
    <div className="esm-subform">
      <h3>
        {creating ? "Define a provider" : `Edit ${target.row.display_name}`}
      </h3>
      <p className="esm-muted-text">
        {creating
          ? "A definition, not an account: it says where the provider is and how a key is presented to it. Nothing routes anywhere until a tenant adds their own key against it."
          : "The provider key is fixed — it is what tenant accounts and the model catalogue join on. Everything else here is a correction, and it moves every tenant already running on this provider."}
      </p>
      <ErrorAlert
        error={create.error ?? update.error}
        title="The template was not saved"
      />
      <div className="esm-form-grid">
        <Field
          label="Provider"
          htmlFor="template-provider"
          hint={
            creating
              ? "Which adapter serves it. Only the providers this build carries can be defined."
              : "Fixed once written."
          }
        >
          <Select
            id="template-provider"
            value={form.provider}
            disabled={!creating}
            onChange={(event) => set("provider")(event.target.value)}
          >
            {PROVIDERS.map((provider) => (
              <option key={provider} value={provider}>
                {provider}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Display name"
          htmlFor="template-display-name"
          hint="What an operator sees in the add-provider picker."
        >
          <Input
            id="template-display-name"
            value={form.display_name}
            onChange={(event) => set("display_name")(event.target.value)}
          />
        </Field>
        <Field
          label="Base URL"
          htmlFor="template-base-url"
          hint="The vendor's https origin — https://api.example.com, with no path. Each adapter composes its own."
          error={urlError}
        >
          <Input
            id="template-base-url"
            value={form.base_url}
            invalid={urlError !== undefined}
            onChange={(event) => set("base_url")(event.target.value)}
          />
        </Field>
        <Field
          label="Auth style"
          htmlFor="template-auth-style"
          hint="Which header the tenant's key is presented in."
        >
          <Select
            id="template-auth-style"
            value={form.auth_style}
            onChange={(event) => set("auth_style")(event.target.value)}
          >
            {AUTH_STYLE_NAMES.map((style) => (
              <option key={style} value={style}>
                {AUTH_STYLES[style]}
              </option>
            ))}
          </Select>
        </Field>
        {creating ? (
          <Field
            label="Status"
            htmlFor="template-status"
            hint="Published is offered to every tenant. Draft is written and not offered."
          >
            <Select
              id="template-status"
              value={form.status}
              onChange={(event) => set("status")(event.target.value)}
            >
              <option value="published">Published</option>
              <option value="draft">Draft</option>
            </Select>
          </Field>
        ) : null}
      </div>
      <Field
        label="Default headers"
        htmlFor="template-headers"
        hint="One 'Name: value' per line, sent on every call to this provider. A version pin or a referer — never a key: the platform refuses the header names a credential travels in, because one here would be one key shared by every tenant."
        error={headerError}
      >
        <textarea
          id="template-headers"
          className="esm-input esm-textarea"
          rows={3}
          value={form.headers}
          onChange={(event) => set("headers")(event.target.value)}
        />
      </Field>
      <div className="esm-actions">
        <ReasonButton
          variant="primary"
          busy={create.isPending || update.isPending}
          disabled={!ready}
          mutation={{
            title: creating ? `Define ${name}` : `Save changes to ${name}`,
            tone: "primary",
            description: creating ? (
              <p>
                A definition of <code>{form.provider}</code> at{" "}
                <code>{form.base_url.trim() || "…"}</code>. It moves no traffic
                on its own — a tenant has to add their own key against it
                first.
              </p>
            ) : (
              <p>
                Every tenant already running on{" "}
                <code>{target.row.provider}</code> reads this row, so a moved
                endpoint moves all of them at once.
              </p>
            ),
            confirmLabel: label,
            run,
          }}
        >
          {label}
        </ReasonButton>
        <Button
          variant="ghost"
          disabled={create.isPending || update.isPending}
          onClick={onClose}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** validateTemplateBaseURL's rule, said before the request rather than after
 *  it. The server is the control; this only spares the round trip. */
function baseURLError(raw: string): string | undefined {
  const text = raw.trim();
  if (text === "") {
    return undefined;
  }
  let parsed: URL;
  try {
    parsed = new URL(text);
  } catch {
    return "Not a URL. It should look like https://api.example.com.";
  }
  if (parsed.protocol !== "https:") {
    return "A template's endpoint is https. A private or plaintext endpoint belongs on the account that overrides it, not on the platform's definition.";
  }
  if (parsed.pathname !== "/" && parsed.pathname !== "") {
    return "An origin, with no path — each adapter composes its own and would otherwise send it twice.";
  }
  if (parsed.search !== "" || parsed.hash !== "") {
    return "An origin, with no query or fragment.";
  }
  return undefined;
}
